/* Wireframes-fully ruling 20/10 § 1 (campaign-first first run) · what the campaign needs and the account lacks, asked inside
   the builder at the step that needs it, as an inline card the merchant fills or skips. Step 1: the establishment's name and
   its Google fiche — the fiche path is GuideEtablissement's own (readFiche: the demo links Google at once; with an API the
   server reads the fiche and creates the Google platform, POST …/locations/:id/fiche), the name by hand with the address if
   given (setAdresse; the name alone, setLocationName since lib-pack-22: the API's locations.name / address), « Plus tard » the explicit answer (setFicheLater, FQ-C4). Nothing here is
   a condition: « Suivant » and « Mettre en ligne » keep today's checks. Step 2's card (the gift and the roue) lives in
   Stepper.tsx, beside the « Le cadeau » select it fills. */
import { useState } from 'react'
import { Button, Card, Field, Input, useToast } from '@/ui'
import { apiWrites } from '@/lib/api'
import { getAdresse, getDestinations, getFiche, getFicheLater, readFiche, setAdresse, setFicheLater, setLocationName } from '@/lib/campaign'
import { editRefusalLines, settled, writeMark } from '@/lib/writes'

/** True when the establishment has neither a fiche (read, or a validated Google platform), nor an address typed by hand, nor
    an explicit « Plus tard »: step 1 then shows the card. A name typed alone does not count (the API keeps no address then,
    and the demo keeps ''), so both modes ask again on the next campaign; « Plus tard » is the answer that stops it. */
export function needsEtablissement(locId: string): boolean {
  return !getFiche() && !getAdresse()?.address && !getFicheLater()
    && !getDestinations().some(d => d.locationId === locId && d.platform === 'google' && d.state === 'validee')
}

type Etat = 'ask' | 'manual' | 'linked' | 'adresse' | 'later'

/** Step 1 · « Votre établissement » — `onChange(google)` after each answer; `google` is true once the fiche linked Google. */
export function CarteEtablissement({ locName, onChange }: { locName: string; onChange: (google: boolean) => void }) {
  const toast = useToast()
  const [etat, setEtat] = useState<Etat>('ask')
  const [url, setUrl] = useState('')
  const [busy, setBusy] = useState(false)
  const [manual, setManual] = useState({ name: locName, address: '' })
  const name = getFiche()?.name || getAdresse()?.name || locName
  const verifier = () => {
    if (!url.trim() || busy) return
    const mark = writeMark()
    const f = readFiche(url.trim(), locName)
    setUrl(''); setFicheLater(false)
    if (!apiWrites) { toast({ message: `✓ Fiche lue : ${f.name} · ${f.rating} ★. Google est lié.` }); setEtat('linked'); onChange(true); return }
    /* the server reads the fiche when it can (a Places key); without one it keeps the link — Google is linked all the same */
    setBusy(true)
    void settled(mark).then(no => {
      setBusy(false)
      if (no) { toast({ message: editRefusalLines(no)[0] }); return }
      const read = getFiche()
      toast({ message: read?.rating ? `✓ Fiche lue : ${read.name} · ${read.rating} ★. Google est lié.` : '✓ Lien enregistré : Google est lié.' })
      setEtat('linked'); onChange(true)
    })
  }
  /* point 9's Stepper touch (lib-pack-22 rider, point-1 vet § For B): the address is optional; the name alone is one
     PATCH { name }. With an API the card says the server's answer before it moves on. */
  const saveManual = () => {
    const a = { name: manual.name.trim(), address: manual.address.trim() }
    if (!a.name || busy) return
    const mark = writeMark()
    if (a.address) setAdresse(a); else setLocationName(a.name)
    const ok = a.address ? '✓ Adresse enregistrée.' : '✓ Nom enregistré.'
    const done = () => { setFicheLater(false); setEtat('adresse'); onChange(false); toast({ message: ok }) }
    if (!apiWrites) { done(); return }
    setBusy(true)
    void settled(mark).then(no => { setBusy(false); if (no) toast({ message: editRefusalLines(no)[0] }); else done() })
  }
  const later = () => { setFicheLater(true); setEtat('later'); onChange(false) }
  const sansFiche = 'Sans fiche, vos campagnes ne demandent pas d’avis Google. Vous la lierez dans Paramètres › Plateformes.'
  return (
    <Card data-testid="carte-etablissement">
      <p className="bld-t">Votre établissement <span className="f-help">· facultatif</span></p>
      {etat === 'linked' ? (
        <p className="f-help" style={{ margin: 0 }} data-testid="carte-etablissement-fait"><b>✓ {name} · fiche liée.</b> Google est lié&nbsp;: «&nbsp;Avis Google&nbsp;» est proposé dans la demande ci-dessous.</p>
      ) : etat === 'adresse' ? (
        <p className="f-help" style={{ margin: 0 }} data-testid="carte-etablissement-fait"><b>✓ {[manual.name.trim(), manual.address.trim()].filter(Boolean).join(' · ')}</b>, saisi{manual.address.trim() ? 'e' : ''} à la main. {sansFiche}</p>
      ) : etat === 'later' ? (
        <p className="f-help" style={{ margin: 0 }} data-testid="carte-etablissement-fait"><b>Plus tard, d’accord.</b> {sansFiche} <a href="#" onClick={e => { e.preventDefault(); setFicheLater(false); setEtat('ask'); onChange(false) }}>Annuler</a></p>
      ) : (<>
        <p className="t-body" style={{ margin: '2px 0 12px' }}>«&nbsp;{name}&nbsp;»&nbsp;: le nom, l’adresse et les horaires viennent de votre fiche Google, et les avis Google y mènent. Vous pouvez aussi le faire plus tard.</p>
        {etat === 'manual' ? (
          <div style={{ display: 'grid', gap: 10 }} data-testid="carte-adresse-main">
            <Field label="Le nom de l’établissement">{id => <Input id={id} maxLength={80} value={manual.name} onChange={e => setManual({ ...manual, name: e.target.value })} />}</Field>
            <Field label="L’adresse (facultatif)">{id => <Input id={id} maxLength={200} value={manual.address} onChange={e => setManual({ ...manual, address: e.target.value })} onKeyDown={e => { if (e.key === 'Enter') saveManual() }} placeholder="12 rue de la Paix, 75002 Paris" />}</Field>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Button onClick={saveManual} disabled={!manual.name.trim() || busy} data-testid="carte-adresse-enregistrer">{manual.address.trim() ? 'Enregistrer l’adresse' : 'Enregistrer le nom'}</Button>
              <Button variant="quiet" onClick={() => setEtat('ask')}>Annuler</Button>
            </div>
          </div>
        ) : (<>
          <Field label="Le lien de votre fiche Google">{id => (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Input id={id} value={url} onChange={e => setUrl(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') verifier() }} placeholder="Collez le lien de votre fiche Google…" style={{ flex: 1, minWidth: 200 }} />
              <Button onClick={verifier} disabled={!url.trim() || busy} data-testid="carte-fiche-verifier">Vérifier</Button>
            </div>
          )}</Field>
          <p className="f-help" style={{ margin: '10px 0 0', display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>Pas de fiche Google pour l’instant&nbsp;?
            <Button size="sm" variant="quiet" data-testid="carte-saisir-adresse" onClick={() => { setManual({ name, address: '' }); setEtat('manual') }}>Saisir le nom et l’adresse à la main</Button>
            <Button size="sm" variant="quiet" data-testid="carte-plus-tard" onClick={later}>Plus tard</Button></p>
        </>)}
      </>)}
    </Card>
  )
}
